import { ConfigApi, DiscoveryApi, FetchApi } from "@backstage/frontend-plugin-api";
import {
  B2CApi,
  Environment,
  RedirectUris,
  UriType,
  Idps,
  ScIdpRole,
  TenantsAndSystems,
  AuthorizedSystem,
  SystemDomain,
  Tenant,
  RouteEntry,
  VirtualHost,
  MatchType,
  RewriteType,
  CookieMatcher,
  CookieMatchType,
  TenantInfo
} from "@internal/backstage-plugin-b2c-common";
import * as yaml from 'js-yaml';

export class B2CApiImpl implements B2CApi {
  fecthApi: FetchApi;
  fileApiUrl: string;
  discoveryApi: DiscoveryApi;

  constructor(options: {
    fetchApi: FetchApi;
    configApi: ConfigApi;
    discoveryApi: DiscoveryApi;
  }) {
    this.fecthApi = options.fetchApi;
    const backstageBaseUrl = options.configApi.getString('backend.baseUrl');
    this.fileApiUrl = `${backstageBaseUrl}/api/fileproxy`;
    this.discoveryApi = options.discoveryApi;
  }

  private async getYamlContent<T>(options: { url: string }): Promise<T> {
    const response = await this.fecthApi.fetch(options.url);
    const responseContent = await response.text();
    if (!response.ok) {
      throw new Error(responseContent);
    }

    const yamlContent = yaml.load(responseContent) as T;
    return yamlContent;
  }

  async getRedirectUris(options: { environment: Environment; }): Promise<RedirectUris> {
    const url = `${this.fileApiUrl}?link=https://dev.azure.com/SimCorpCloud/AzureB2C/_git/AzureB2C?path=/definitions/redirect-uris-${options.environment.toLowerCase()}.yml`;
    const docs = await this.getYamlContent<{ web: string[], spa: string[] }>({ url });

    const uris = docs.web.map(x => ({ type: UriType.WEB, uri: x }))
      .concat(docs.spa.map(x => ({ type: UriType.SPA, uri: x})));
    return { uris };
  }

  async getIdpConfig(options: { environment: Environment; }): Promise<Idps> {
    const url = `${this.fileApiUrl}?link=https://dev.azure.com/SimCorpCloud/AzureB2C/_git/AzureB2C?path=/definitions/idp-${options.environment}.yml`;
    return await this.getYamlContent<Idps>({ url });
  }

  async getScIdpRoles(): Promise<ScIdpRole[]> {
    const url = `${this.fileApiUrl}?link=https://dev.azure.com/SimCorpCloud/AzureB2C/_git/SimCorpCloudIdentityProvider?path=/definitions/appRegistrations.json`;
    const response = await this.fecthApi.fetch(url);
    const responseContent = await response.json();
    if (!response.ok) {
      throw new Error(responseContent);
    }

    const scIdpRoles = responseContent.definitions[0].appRoles as ScIdpRole[];
    return scIdpRoles;
  }

  async getTenantInfo(options: { environment: Environment }): Promise<TenantInfo> {
    const apiUrl = await this.discoveryApi.getBaseUrl('b2c');
    const response = await this.fecthApi.fetch(`${apiUrl}/tenantInfo?environment=${options.environment.toLowerCase()}`);
    if (response.status === 200) {
      return (await response.json()) as TenantInfo;
    }

    throw new Error('Error getting B2C tenant information');
  }

  async getAuthorizedTenantsAndSystems(options: { environment: Environment; }): Promise<AuthorizedSystem[]> {
    const url = `${this.fileApiUrl}?link=https://dev.azure.com/SimCorpCloud/AzureB2C/_git/AuthorizationProxy?path=/manifest/authz-server-config/${options.environment.toLowerCase()}/authz-config.yml`;
    const authorizedSystems = [] as AuthorizedSystem[];
    const tenantsAndSystems =  await this.getYamlContent<TenantsAndSystems>({ url });
    tenantsAndSystems.systems.forEach((systemDomain: SystemDomain) => {
      const tenants = tenantsAndSystems.tenants
        .filter((tenant: Tenant) => tenant.systems.includes(systemDomain.domain))
        .map(x => x.name);

      const authorizedSystem = {
        ...systemDomain,
        tenants,
      } as AuthorizedSystem;
      if (authorizedSystem.authenticated === undefined || authorizedSystem.authenticated === null) {
        authorizedSystem.authenticated = []
      }
      if (authorizedSystem.unauthenticated === undefined || authorizedSystem.unauthenticated === null) {
        authorizedSystem.unauthenticated = []
      }
      authorizedSystems.push(authorizedSystem);
    });
    return authorizedSystems;
  }

  async getVirtualHosts(options: { environment: Environment; }): Promise<VirtualHost[]> {
    const url = `${this.fileApiUrl}?link=https://dev.azure.com/SimCorpCloud/AzureB2C/_git/AuthorizationProxy?path=/envoy-configuration-params/${options.environment}/envoy-def-${options.environment.toLowerCase()}.yaml`;
    const returnVal = [] as VirtualHost[];
    const { virtualHosts } = await this.getYamlContent<{ virtualHosts: any }>({ url });

    virtualHosts.forEach((element: any) => {
      const { domain, routes } = element;
      const orderedRoutes = routes.map((routeElement: any, order: number) => {
        const { path, pathSeparatedPrefix, prefix, regex, cookies: rawCookies } = routeElement.match;
        const { hostRewrite, prefixRewrite, regexRewrite, timeoutSeconds, healthCheckPath } = routeElement.route;
        let matchType;
        let match;
        if (path !== undefined) {
          matchType = MatchType.PATH;
          match = path;
        } else if (pathSeparatedPrefix !== undefined) {
          matchType = MatchType.PATHSEPARATEDPREFIX;
          match = pathSeparatedPrefix;
        } else if (prefix !== undefined) {
          matchType = MatchType.PREFIX;
          match = prefix;
        } else if (regex !== undefined) {
          matchType = MatchType.REGEX;
          match = regex;
        }

        let rewriteType;
        let rewrite;
        let substitution;
        if (prefixRewrite !== undefined) {
          rewriteType = RewriteType.PREFIXREWRITE;
          rewrite = prefixRewrite;
        } else if (regexRewrite !== undefined) {
          rewriteType = RewriteType.REGEXREWRITE;
          rewrite = regexRewrite.regex;
          substitution = regexRewrite.substitution;
        } else {
          rewriteType = RewriteType.NONE;
        }

        const useCustomTimeout = (timeoutSeconds !== undefined);

        let cookies: CookieMatcher[] | undefined;
        if (rawCookies && Array.isArray(rawCookies) && rawCookies.length > 0) {
          cookies = rawCookies.map((c: any) => {
            const { name, stringMatch } = c;
            if (!stringMatch) return null;
            let cookieMatchType: CookieMatchType;
            let cookieMatchValue: string;
            if (stringMatch.exact !== undefined) {
              cookieMatchType = CookieMatchType.EXACT;
              cookieMatchValue = stringMatch.exact;
            } else {
              cookieMatchType = CookieMatchType.SAFEREGEX;
              cookieMatchValue = stringMatch.safeRegex;
            }
            return {
              name,
              cookieMatchType,
              cookieMatchValue,
              ignoreCase: stringMatch.ignoreCase === true ? true : undefined,
            } as CookieMatcher;
          }).filter(Boolean) as CookieMatcher[];
        }

        const routeEntry = {
          order,
          host: hostRewrite,
          healthCheckPath,
          matchType,
          match,
          rewriteType,
          rewrite,
          substitution,
          useCustomTimeout,
          timeoutSeconds,
          cookies
        } as RouteEntry;

        return routeEntry;
      });

      returnVal.push({
        domain,
        routes: orderedRoutes
      });
    });

    return returnVal;
  }
}
